import { useState } from 'react';

/**
 * A key/value pair editor for arbitrary JSON-object "params" fields (demand-partner params
 * overrides, etc). Kept deliberately as structured rows rather than a raw JSON textarea per the
 * spec — friendlier for two non-technical-in-the-moment founders editing partner site IDs.
 * Values are stored/edited as strings; onChange emits a plain object, attempting a JSON.parse on
 * each value so numbers/booleans round-trip, falling back to the raw string otherwise.
 */
export default function KeyValueEditor({ value, onChange, disabled }) {
  const [rows, setRows] = useState(() => toRows(value));

  function toRows(obj) {
    const entries = Object.entries(obj || {});
    return entries.length ? entries.map(([k, v]) => ({ key: k, value: stringify(v) })) : [{ key: '', value: '' }];
  }

  function stringify(v) {
    if (typeof v === 'string') return v;
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  }

  function emit(nextRows) {
    setRows(nextRows);
    const obj = {};
    nextRows.forEach(({ key, value: v }) => {
      if (!key.trim()) return;
      let parsed = v;
      if (v === 'true') parsed = true;
      else if (v === 'false') parsed = false;
      else if (v !== '' && !Number.isNaN(Number(v))) parsed = Number(v);
      obj[key.trim()] = parsed;
    });
    onChange(obj);
  }

  function updateRow(i, patch) {
    const next = rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r));
    emit(next);
  }

  function addRow() {
    emit([...rows, { key: '', value: '' }]);
  }

  function removeRow(i) {
    const next = rows.filter((_, idx) => idx !== i);
    emit(next.length ? next : [{ key: '', value: '' }]);
  }

  return (
    <div className="kv-editor">
      {rows.map((row, i) => (
        <div className="kv-editor__row" key={i}>
          <input
            className="kv-editor__key"
            placeholder="key"
            value={row.key}
            disabled={disabled}
            onChange={(e) => updateRow(i, { key: e.target.value })}
          />
          <input
            className="kv-editor__value"
            placeholder="value"
            value={row.value}
            disabled={disabled}
            onChange={(e) => updateRow(i, { value: e.target.value })}
          />
          <button
            type="button"
            className="kv-editor__remove"
            disabled={disabled}
            onClick={() => removeRow(i)}
            aria-label="Remove row"
          >
            &times;
          </button>
        </div>
      ))}
      <button type="button" className="btn btn--ghost btn--sm" onClick={addRow} disabled={disabled}>
        + Add parameter
      </button>
    </div>
  );
}
